import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import "../../../styles/panel/user/ManageShops.css";
import { API_BASE_URL } from "../../../api/http";
import { fetchMyStores, fetchSupportStores } from "../../../api/storeRequest";
import moment from "jalali-moment";
import {
  Icon,
  ICONS,
  MY_SITES_PATH,
  GUIDE_STEPS,
} from "./manageShop/shared/EditShopShared";

export default function ManageShops() {
  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  const [guideOpen, setGuideOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [shops, setShop] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusTab, setStatusTab] = useState("active");
  const [qrShop, setQrShop] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const qrWrapRef = useRef(null);
  const isSupport = role === "support";

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const res =
        role == "user"
          ? await fetchMyStores()
          : role == "support"
            ? await fetchSupportStores()
            : [];
      setShop(res.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // آیا اشتراک فروشگاه هنوز معتبره؟
  const isActive = (shop) => {
    if (!shop.end_date) return false;
    return moment(shop.end_date).isSameOrAfter(moment());
  };

  const expiredShops = shops.filter((shop) => !isActive(shop));
  const activeShops = shops.filter((shop) => isActive(shop));

  const matchesSearch = (shop) =>
    (shop.store_name || "").toLowerCase().includes(search.trim().toLowerCase());

  const filteredShops = shops.filter(matchesSearch);

  // برای کاربر پشتیبان: فروشگاه‌ها بر اساس تب انتخابی (فعال/منقضی) فیلتر میشن
  const visibleShops = isSupport
    ? (statusTab === "active" ? activeShops : expiredShops).filter(
        matchesSearch,
      )
    : filteredShops;

  // با کلیک روی کارت فروشگاه، وارد صفحه‌ی مدیریت همون فروشگاه (دکمه‌های عملیات) میشیم
  const openShop = (shop) => {
    navigate(`${shop.store_id}`, { state: { shop } });
  };

  // آدرس عمومی فروشگاه که داخل QR قرار می‌گیره
  const getShopUrl = (shop) => `${window.location.origin}/${shop.store_slug}`;

  // دانلود QR به صورت تصویر PNG
  const downloadQr = () => {
    const canvas = qrWrapRef.current?.querySelector("canvas");
    if (!canvas || !qrShop) return;
    try {
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `${qrShop.store_slug}-qr.png`;
      link.click();
    } catch (err) {
      // اگه لوگو cross-origin باشه و CORS فعال نباشه canvas «tainted» میشه
      console.log(err);
    }
  };

  // کپی آدرس فروشگاه در کلیپ‌بورد
  const copyShopUrl = async (shop) => {
    const url = getShopUrl(shop);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // برای مرورگرهایی که clipboard API ندارن یا روی http هستن
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedId(shop.store_id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  return (
    <div className="shop-manage">
      <div className="sm-head">
        <div>
          <h1>مدیریت فروشگاه</h1>
          <p>اطلاعات و بخش‌های اصلی فروشگاهت رو از همین‌جا مدیریت کن.</p>
        </div>
        <div className="sm-head-actions">
          <div className="sm-search">
            <Icon>{ICONS.search}</Icon>
            <input
              type="text"
              inputMode="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="جستجو بر اساس نام فروشگاه"
            />
            {search && (
              <button
                type="button"
                className="sm-search-clear"
                onClick={() => setSearch("")}
                aria-label="پاک کردن جستجو"
              >
                <Icon>{ICONS.close}</Icon>
              </button>
            )}
          </div>
          {!isSupport && (
            <button
              type="button"
              className="sm-notify-btn"
              onClick={() => setNotifyOpen(true)}
              aria-label="نوتیفیکیشن"
            >
              <Icon>{ICONS.bell}</Icon>
              {expiredShops.length > 0 && (
                <span className="sm-notify-badge">{expiredShops.length}</span>
              )}
            </button>
          )}
          <button className="sm-help-btn" onClick={() => setGuideOpen(true)}>
            <Icon>{ICONS.help}</Icon>
            راهنما
          </button>
        </div>
      </div>

      {isSupport && !loading && shops.length > 0 && (
        <div className="sm-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={statusTab === "active"}
            className={`sm-tab ${statusTab === "active" ? "sm-tab--active" : ""}`}
            onClick={() => setStatusTab("active")}
          >
            فروشگاه‌های فعال
            <span className="sm-tab-count">{activeShops.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={statusTab === "expired"}
            className={`sm-tab ${statusTab === "expired" ? "sm-tab--active" : ""}`}
            onClick={() => setStatusTab("expired")}
          >
            فروشگاه‌های منقضی
            <span className="sm-tab-count">{expiredShops.length}</span>
          </button>
        </div>
      )}

      {loading ? (
        <div className="sm-empty">
          <p>در حال دریافت فروشگاه‌ها...</p>
        </div>
      ) : shops.length === 0 ? (
        <div className="sm-empty">
          <div className="sm-empty-ic">
            <Icon>{ICONS.showcase}</Icon>
          </div>
          <h3>هنوز فروشگاهی نداری</h3>
          <p>با ثبت اولین فروشگاهت می‌تونی از همین‌جا مدیریتش کنی.</p>
        </div>
      ) : visibleShops.length === 0 ? (
        <div className="sm-empty">
          <div className="sm-empty-ic">
            <Icon>{ICONS.search}</Icon>
          </div>
          <h3>
            {isSupport
              ? statusTab === "active"
                ? "فروشگاه فعالی پیدا نشد"
                : "فروشگاه منقضی‌ای پیدا نشد"
              : "فروشگاهی پیدا نشد"}
          </h3>
          <p>
            {search
              ? "فروشگاهی با این نام پیدا نشد. عبارت جستجو رو بررسی کن."
              : isSupport && statusTab === "active"
                ? "در حال حاضر فروشگاه فعالی در این لیست وجود نداره."
                : isSupport && statusTab === "expired"
                  ? "در حال حاضر فروشگاه منقضی‌ای در این لیست وجود نداره."
                  : "فروشگاهی با این نام پیدا نشد. عبارت جستجو رو بررسی کن."}
          </p>
        </div>
      ) : (
        <div className="sm-shop-list">
          {visibleShops.map((shop) => {
            const active = isActive(shop);
            return (
              <div
                className="sm-gcard"
                key={shop.store_id}
                role="button"
                tabIndex={0}
                onClick={() => openShop(shop)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openShop(shop);
                  }
                }}
              >
                <div className="sm-gcard-top">
                  {shop.logo ? (
                    <img
                      className="sm-gcard-logo"
                      src={`${API_BASE_URL}${shop.logo}`}
                      alt={shop.store_name || "Shop logo"}
                    />
                  ) : (
                    <div className="sm-gcard-logo sm-gcard-logo--placeholder">
                      {shop.store_name?.slice(0, 2) || "ف"}
                    </div>
                  )}
                  <div className="sm-gcard-info">
                    <h2>{shop.store_name}</h2>
                    <div className="sm-gcard-linkrow">
                      <a
                        className="sm-link"
                        href={`/${shop.store_slug}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Icon>{ICONS.link}</Icon>
                        {shop.store_slug}
                      </a>
                      <button
                        type="button"
                        className={`sm-copy-btn ${
                          copiedId === shop.store_id ? "sm-copy-btn--done" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyShopUrl(shop);
                        }}
                        aria-label="کپی آدرس فروشگاه"
                        title={
                          copiedId === shop.store_id ? "کپی شد" : "کپی آدرس"
                        }
                      >
                        {copiedId === shop.store_id ? (
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        ) : (
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="9" y="9" width="12" height="12" rx="2" />
                            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <span className="sm-gcard-chevron">
                    <Icon>{ICONS.chevron}</Icon>
                  </span>
                </div>

                <div className="sm-gcard-foot">
                  <span
                    className={`sm-badge ${
                      active ? "sm-badge--active" : "sm-badge--expired"
                    }`}
                  >
                    <span className="sm-badge-dot" />
                    {active ? "فعال" : "منقضی"}
                    <span className="sm-badge-date">
                      {moment(shop.end_date).format("jYYYY/jMM/jDD")}
                    </span>
                  </span>

                  <div className="sm-gcard-actions">
                    {active && (
                      <button
                        type="button"
                        className="sm-gcard-qr"
                        onClick={(e) => {
                          e.stopPropagation();
                          setQrShop(shop);
                        }}
                        aria-label="QR Code"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="3" width="7" height="7" rx="1" />
                          <rect x="14" y="3" width="7" height="7" rx="1" />
                          <rect x="3" y="14" width="7" height="7" rx="1" />
                          <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20h1" />
                        </svg>
                        <span className="sm-gcard-qr-label">QR</span>
                      </button>
                    )}

                    {!active && !isSupport && (
                      <button
                        type="button"
                        className="sm-gcard-renew"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(MY_SITES_PATH);
                        }}
                      >
                        تمدید
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {guideOpen && (
        <div
          className="sm-overlay"
          onClick={(e) => e.target === e.currentTarget && setGuideOpen(false)}
        >
          <div className="sm-modal sm-modal--guide">
            <div className="sm-modal-head">
              <div>
                <h3>راهنمای مدیریت فروشگاه</h3>
                <p>
                  هر فروشگاهی که داشته باشید اینجا نمایش داده می‌شود. روی هر
                  فروشگاه که کلیک کنید، وارد صفحه‌ی مدیریت همون فروشگاه می‌شید.
                  برای راه‌اندازی سریع، این مراحل رو به ترتیب انجام بدید:
                </p>
              </div>
              <button
                className="sm-modal-close"
                onClick={() => setGuideOpen(false)}
                aria-label="بستن"
              >
                <Icon>{ICONS.close}</Icon>
              </button>
            </div>

            <a
              className="sm-video-link"
              href="https://aparat.com/v/phd5850"
              target="_blank"
              rel="noreferrer"
            >
              <Icon>{ICONS.link}</Icon>
              مشاهده آموزش ویدیویی
            </a>

            <ul className="sm-steps sm-steps--guide">
              {GUIDE_STEPS.map((step, i) => (
                <li className="sm-step" key={step.title}>
                  <span className="sm-step-num">{i + 1}</span>
                  <div>
                    <h4>{step.title}</h4>
                    <p>{step.text}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="sm-modal-foot">
              <button
                className="sm-primary-btn"
                onClick={() => setGuideOpen(false)}
              >
                متوجه شدم
              </button>
            </div>
          </div>
        </div>
      )}

      {notifyOpen && (
        <div
          className="sm-overlay"
          onClick={(e) => e.target === e.currentTarget && setNotifyOpen(false)}
        >
          <div className="sm-modal">
            <div className="sm-modal-head">
              <div>
                <h3>نیاز به بررسی اشتراک</h3>
              </div>
              <button
                className="sm-modal-close"
                onClick={() => setNotifyOpen(false)}
                aria-label="بستن"
              >
                <Icon>{ICONS.close}</Icon>
              </button>
            </div>

            {expiredShops.length > 0 ? (
              <>
                <ul className="sm-expired-list">
                  {expiredShops.map((shop) => (
                    <li className="sm-expired-item" key={shop.store_id}>
                      {shop.logo ? (
                        <img
                          className="sm-expired-logo"
                          src={`${API_BASE_URL}${shop.logo}`}
                          alt={shop.store_name || "Shop logo"}
                        />
                      ) : (
                        <div className="sm-expired-logo sm-expired-logo-placeholder">
                          {shop.store_name?.slice(0, 2) || "ف"}
                        </div>
                      )}
                      <div className="sm-expired-info">
                        <span className="sm-expired-name">
                          {shop.store_name}
                        </span>
                        <span className="sm-expired-date">
                          پایان اعتبار{" "}
                          {moment(shop.end_date).format("jYYYY/jMM/jDD")}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="sm-modal-foot">
                  <button
                    className="sm-primary-btn"
                    onClick={() => {
                      setNotifyOpen(false);
                      navigate(MY_SITES_PATH);
                    }}
                  >
                    تمدید اشتراک
                  </button>
                </div>
              </>
            ) : (
              <div className="sm-modal-empty">
                <p>همه‌ی فروشگاه‌های شما فعال هستند و نیازی به اقدام نیست.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {qrShop && isActive(qrShop) && (
        <div
          className="sm-overlay"
          onClick={(e) => e.target === e.currentTarget && setQrShop(null)}
        >
          <div className="sm-modal">
            <div className="sm-modal-head">
              <div>
                <h3>QR Code فروشگاه</h3>
                <p>{qrShop.store_name}</p>
              </div>
              <button
                className="sm-modal-close"
                onClick={() => setQrShop(null)}
                aria-label="بستن"
              >
                <Icon>{ICONS.close}</Icon>
              </button>
            </div>

            <div className="sm-qr-body" ref={qrWrapRef}>
              <QRCodeCanvas
                value={getShopUrl(qrShop)}
                size={256}
                level="H"
                marginSize={4}
                bgColor="#ffffff"
                fgColor="#000000"
                imageSettings={
                  qrShop.logo
                    ? {
                        src: `${API_BASE_URL}${qrShop.logo}`,
                        height: 48,
                        width: 48,
                        excavate: true,
                        crossOrigin: "anonymous",
                      }
                    : undefined
                }
              />
              <span className="sm-qr-url" dir="ltr">
                {getShopUrl(qrShop)}
              </span>
            </div>

            <div className="sm-modal-foot">
              <button className="sm-primary-btn" onClick={downloadQr}>
                دانلود QR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
